/**
 * Sample data used when VITE_USE_MOCK=true, so the screens can be developed and
 * demoed without the Kit19 REST API running. Shapes match what the API returns,
 * not what the UI wants — the same mappers run over both.
 */
import type {
  ActivityItem,
  AppointmentItem,
  CallLogDetail,
  CallLogItem,
  ChatLogItem,
  CommentItem,
  CustomFieldDef,
  CustomFieldValue,
  DocumentItem,
  FollowUpItem,
  HashedUser,
  InteractionHistory,
  LeadScore,
  NoteItem,
  Option,
  PhysicalAppointmentDetail,
  TaskDetail,
  TaskItem,
  WebformItem,
} from 'types/entity'
import type { LeadDetailResponse } from './mappers'

/**
 * UserCRM/GetLeadDetailByLeadIdNew — the record, its custom fields, its deal
 * stages and its field-mask rows in one response.
 */
export const mockLeadDetailResponse: LeadDetailResponse = {
  PredefinedFields: {
    LeadId: '100916',
    LeadNo: '100916',
    PersonName: 'Bhoomi Mittal',
    CompanyName: 'Kit19 Technologies',
    CountryCode: '+91',
    CountryCode1: '+91',
    CountryCode2: '',
    MobileNo: '9990998798',
    MobileNo1: '9812345678',
    MobileNo2: '',
    EmailID: 'bhoomi.mittal@kit19.com',
    EmailID1: 'accounts@kit19.com',
    EmailID2: '',
    City: 'Beohari',
    State: 'Madhya Pradesh',
    Country: 'India',
    PinCode: '484774',
    ResidentialAddress: 'H-12, Sector 63, Noida, Uttar Pradesh',
    OfficialAddress: 'A-24, Okhla Industrial Area Phase II, New Delhi',
    SourceName: 'Chat',
    MediumName: 'Interested',
    CampaignName: 'Summer Outreach',
    InitialRemarks: 'Interested in the enterprise plan, asked for a revised quotation.',
    CreatedOn: '31-Jul-2023 12:55:24',
    ModifiedOn: '08-Aug-2023 16:22:10',
    CreatedByName: 'kmukesh343',
    LeadTags: 'Hot,Proposal Expiring Soon,Enterprise',
    StageName: 'Proposal Sent',
    NextStatusDate: '16-Aug-2023 10:00:00',
    StageRemark: 'Awaiting purchase-order confirmation.',
    PersonImage: '',
  },
  CustomFields: [
    { fieldid: 101, fieldname: 'Annual Budget', fieldvalue: '₹ 4,50,000' },
    { fieldid: 102, fieldname: 'Industry', fieldvalue: 'Manufacturing' },
    { fieldid: 103, fieldname: 'Team Size', fieldvalue: '40-60' },
    { fieldid: 104, fieldname: 'Preferred Contact Time', fieldvalue: 'After 4 PM' },
  ],
  dealStageList: [
    stage('Sales Pipeline', 'Enquiry', '1', 10, '31-Jul-2023', 'kmukesh343'),
    stage('Sales Pipeline', 'Qualified', '2', 30, '02-Aug-2023', 'kmukesh343'),
    stage('Sales Pipeline', 'Proposal', '3', 60, '08-Aug-2023', 'sneha.k'),
    stage('Sales Pipeline', 'Negotiation', '', 80, '', ''),
    stage('Sales Pipeline', 'Closed Won', '', 100, '', ''),
    stage('Onboarding Pipeline', 'Kickoff', '4', 25, '10-Aug-2023', 'sneha.k'),
    stage('Onboarding Pipeline', 'Data Import', '', 50, '', ''),
    stage('Onboarding Pipeline', 'Go Live', '', 100, '', ''),
  ],
  maskdata: [],
}

/** Builds a deal-stage row; an empty statusId means the stage is not yet reached. */
function stage(
  pipeline: string,
  stageName: string,
  statusId: string,
  probability: number,
  createdDate: string,
  createdByName: string,
) {
  return {
    LeadPipeLineStatusId: statusId,
    LeadId: '100916',
    PipelineID: pipeline === 'Sales Pipeline' ? '1' : '2',
    PipeLineName: pipeline,
    PipeLineStageID: statusId,
    PipeLineStageName: stageName,
    Amount: 450000,
    Probability: probability,
    FullName: createdByName,
    ProfileImgFileName: '',
    CreatedDate: createdDate,
    CreatedBy: createdByName,
    CreatedByName: createdByName,
    Remarks: '',
    EstimatedClosureDate: '30-Sep-2023',
    Owner: 'sneha.k',
  }
}

/** UserCRM/GetEnquiryDetailByIdNew */
export const mockEnquiryDetail: Record<string, string> = {
  EnquiryId: 'ENQ-20451',
  PersonName: 'Rahul Verma',
  CompanyName: 'Verma Traders',
  CountryCode: '+91',
  CountryCode1: '+91',
  MobileNo: '9876543210',
  MobileNo1: '9811122233',
  MobileNo2: '',
  EmailID: 'rahul@vermatraders.in',
  EmailID1: 'sales@vermatraders.in',
  EmailID2: '',
  City: 'Jaipur',
  State: 'Rajasthan',
  Country: 'India',
  PinCode: '302001',
  ResidentialAddress: '22, Civil Lines, Jaipur',
  OfficialAddress: 'Shop 4, MI Road, Jaipur',
  SourceName: 'Website',
  MediumName: 'Organic',
  CampaignName: 'Website Enquiry Form',
  InitialRemarks: 'Wants a demo of the CRM module for a 40-seat team.',
  CreatedOn: '12-Aug-2023 09:14:02',
  CreatedByName: 'sneha.k',
  EnquiryTags: 'Warm,Demo Requested',
  StageName: 'Demo Scheduled',
  NextStatusDate: '20-Aug-2023 11:00:00',
  StageRemark: 'Demo confirmed for Monday.',
}

/**
 * UserCRM/GetDependentFieldData — "Sub Category" (108) is a child of
 * "Industry" (102): its choices change with the selected industry.
 */
export const mockDependentFields = [
  {
    FieldId: '108',
    Choices: 'Auto Components,Textiles,Chemicals,Apparel,Grocery,Consulting',
    FormulaId: '',
    ParentFieldId: '102',
    DependencyJson: JSON.stringify([
      { ParentValue: 'Manufacturing', ChildValue: ['Auto Components', 'Textiles', 'Chemicals'] },
      { ParentValue: 'Retail', ChildValue: ['Apparel', 'Grocery'] },
      { ParentValue: 'Services', ChildValue: ['Consulting'] },
    ]),
    DefaultValue: '',
  },
]

/** Enquiry custom fields (the lead's come bundled in its detail response). */
export const mockViewCustomFields: CustomFieldValue[] = [
  { label: 'Annual Budget', value: '₹ 4,50,000' },
  { label: 'Industry', value: 'Manufacturing' },
  { label: 'Team Size', value: '40-60' },
  { label: 'Preferred Contact Time', value: 'After 4 PM' },
]

export const mockEditCustomFields: CustomFieldDef[] = [
  {
    field_ID: 101,
    fieldname: 'Annual Budget',
    datatype: 'Numeric',
    fieldvalue: '450000',
    choices: '',
    isMandatory: true,
    minvalue: '0',
    maxvalue: '10000000',
    defaultValue: '0',
  },
  {
    field_ID: 102,
    fieldname: 'Industry',
    datatype: 'Select',
    fieldvalue: 'Manufacturing',
    choices: 'Manufacturing,Retail,Services,Education,Healthcare',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: 'Retail',
  },
  {
    field_ID: 108,
    fieldname: 'Sub Category',
    datatype: 'Select',
    fieldvalue: 'Textiles',
    choices: 'Auto Components,Textiles,Chemicals,Apparel,Grocery,Consulting',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: '',
    // Child of "Industry" (102) — its choices narrow to the selected industry.
    ParentFieldId: '102',
    ParentFieldValue: 'Manufacturing',
  },
  {
    field_ID: 103,
    fieldname: 'Expected Closure',
    datatype: 'Date',
    fieldvalue: '2023-09-30',
    choices: '',
    isMandatory: false,
    minvalue: '2023-01-01',
    maxvalue: '2026-12-31',
    defaultValue: '',
  },
  {
    field_ID: 104,
    fieldname: 'Decision Maker',
    datatype: 'Radio',
    fieldvalue: 'Yes',
    choices: 'Yes,No,Unknown',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: 'Unknown',
  },
  {
    field_ID: 105,
    fieldname: 'Products Interested',
    datatype: 'List',
    fieldvalue: 'CRM,Telephony',
    choices: 'CRM,Telephony,WhatsApp,Email Marketing,Invoicing',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: '',
  },
  {
    field_ID: 106,
    fieldname: 'Send Newsletter',
    datatype: 'Checkbox',
    fieldvalue: 'True',
    choices: '',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: 'False',
  },
  {
    field_ID: 107,
    fieldname: 'Requirement Notes',
    datatype: 'Multiline',
    fieldvalue: 'Needs migration from an in-house tool; 3 years of history to import.',
    choices: '',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: '',
  },
]

// Pipeline stages now arrive inside mockLeadDetailResponse.dealStageList,
// mirroring GetLeadDetailByLeadIdNew — there is no separate pipeline call.

export const mockLeadScore: LeadScore = {
  score: 72,
  personName: 'Bhoomi Mittal',
  companyName: 'Kit19 Technologies',
  details: [
    { label: 'Email engagement', value: '18 / 25' },
    { label: 'Call responsiveness', value: '20 / 25' },
    { label: 'Website visits', value: '16 / 25' },
    { label: 'Profile completeness', value: '18 / 25' },
  ],
}

export const mockActivities: ActivityItem[] = [
  { id: 'a1', type: 'Email', title: 'Quotation sent', description: 'Revised quotation Q-1182 emailed to the customer.', createdOn: '08-Aug-2023 16:22', createdBy: 'sneha.k', iconUrl: '' },
  { id: 'a2', type: 'Call', title: 'Outbound call — 4m 12s', description: 'Discussed licence count and rollout timeline.', createdOn: '07-Aug-2023 11:05', createdBy: 'kmukesh343', iconUrl: '' },
  { id: 'a3', type: 'Note', title: 'Note added', description: 'Procurement wants a 3-year price lock.', createdOn: '05-Aug-2023 09:47', createdBy: 'kmukesh343', iconUrl: '' },
  { id: 'a4', type: 'Lifecycle', title: 'Stage changed', description: 'Qualified → Proposal', createdOn: '02-Aug-2023 15:31', createdBy: 'kmukesh343', iconUrl: '' },
  { id: 'a5', type: 'WebForm', title: 'Form submitted', description: 'Pricing enquiry form on the website.', createdOn: '31-Jul-2023 12:55', createdBy: 'System', iconUrl: '' },
]

export const mockNotes: NoteItem[] = [
  { id: 'n1', note: 'Procurement wants a 3-year price lock; escalate to sales head.', createdOn: '05-Aug-2023 09:47', createdBy: 'kmukesh343' },
  { id: 'n2', note: 'Prefers a call after 4 PM on weekdays.', createdOn: '01-Aug-2023 18:10', createdBy: 'sneha.k' },
]

export const mockDocuments: DocumentItem[] = [
  { id: 'd1', fileName: 'Quotation_Q-1182.pdf', filePath: '/TempFiles/Quotation_Q-1182.pdf', uploadedOn: '08-Aug-2023 16:20', uploadedBy: 'sneha.k' },
  { id: 'd2', fileName: 'Requirement_Notes.docx', filePath: '/TempFiles/Requirement_Notes.docx', uploadedOn: '03-Aug-2023 10:02', uploadedBy: 'kmukesh343' },
]

export const mockTasks: TaskItem[] = [
  { id: 't1', subject: 'Share revised pricing sheet', status: 'Open', priority: 'High', dueDate: '18-Aug-2023', assignedTo: 'sneha.k' },
  { id: 't2', subject: 'Collect GST details', status: 'Completed', priority: 'Medium', dueDate: '06-Aug-2023', assignedTo: 'kmukesh343' },
]

export const mockAppointments: AppointmentItem[] = [
  { id: 'ap1', subject: 'Product walkthrough', status: 'Scheduled', startDate: '21-Aug-2023 11:00', endDate: '21-Aug-2023 12:00', location: 'Google Meet', assignedTo: 'sneha.k' },
]

export const mockFollowUps: FollowUpItem[] = [
  { id: 'f1', subject: 'Follow up on quotation', status: 'Pending', followUpDate: '16-Aug-2023 10:00', remarks: 'Check whether the PO is raised.', assignedTo: 'kmukesh343' },
]

export const mockCallLogs: CallLogItem[] = [
  { id: 'c1', callType: 'Outbound', fromNumber: '+91 1140848484', toNumber: '+91 9990998798', callTime: '07-Aug-2023 11:05', duration: '4m 12s', recordingUrl: '', agent: 'kmukesh343' },
  { id: 'c2', callType: 'Inbound', fromNumber: '+91 9990998798', toNumber: '+91 1140848484', callTime: '02-Aug-2023 15:28', duration: '1m 47s', recordingUrl: '', agent: 'sneha.k' },
]

export const mockChatLogs: ChatLogItem[] = [
  { id: 'ch1', direction: 'in', message: 'Hi, can you share the enterprise pricing?', sentOn: '31-Jul-2023 12:52', sender: 'Bhoomi Mittal' },
  { id: 'ch2', direction: 'out', message: 'Sure — sending the pricing sheet over email now.', sentOn: '31-Jul-2023 12:55', sender: 'kmukesh343' },
  { id: 'ch3', direction: 'in', message: 'Received, thanks. Will review with the team.', sentOn: '31-Jul-2023 13:04', sender: 'Bhoomi Mittal' },
]

export const mockWebforms: WebformItem[] = [
  { id: 'w1', formName: 'Pricing Enquiry', submittedOn: '31-Jul-2023 12:55', submittedBy: 'Bhoomi Mittal' },
]

/* ------------------------------------------ secondary entity type samples */

/** `/Date(ms)/` values, exactly as the WebMethods serialise them. */
const dotNetDate = (iso: string) => `/Date(${Date.parse(iso)})/`

export const mockTaskDetail: TaskDetail = {
  Title: 'Share revised pricing sheet',
  Description: 'Send the updated enterprise pricing with the 3-year lock option.',
  Remarks: 'Customer asked for this before their board meeting.',
  Address: 'Google Meet',
  CompleteAddress: 'https://meet.google.com/abc-defg-hij',
  StartDate: dotNetDate('2023-08-14T10:00:00'),
  EndDate: dotNetDate('2023-08-18T17:30:00'),
  CreatedOn: dotNetDate('2023-08-09T11:20:00'),
  CompletedOn: '',
  OutcomeName: 'Follow-up required',
  OwnerName: 'sneha.k',
  CreatedBy: 'U1001',
  LeadView: {
    LeadId: '100916',
    PersonName: 'Bhoomi Mittal',
    ProfileImgFileName: '',
  },
  TaskCollaborationUsers: [{ PersonName: 'kmukesh343' }, { PersonName: 'anita.r' }],
  hasComment: true,
}

export const mockPhysicalAppointment: PhysicalAppointmentDetail = {
  Title: 'On-site requirement study',
  Description: 'Visit the plant to map the existing workflow before migration.',
  geGeoEntityName: 'Verma Traders — Jaipur plant',
  StartDate: '21-Aug-2023 10:00',
  EndDate: '21-Aug-2023 16:00',
  CompletedOn: '',
  CreatedDate: '12-Aug-2023 09:30',
  auDisplayName: 'sneha.k',
  FieldExecutives: JSON.stringify([{ fuDisplayName: 'Ravi Sharma' }, { fuDisplayName: 'Anita Rao' }]),
  Collaborators: JSON.stringify([{ fuDisplayName: 'kmukesh343' }]),
}

export const mockCallLogDetail: CallLogDetail = {
  PersonName: 'Bhoomi Mittal',
  ProfileImgFileName: '',
  MobileNo: '+91 9990998798',
  EntityId: '100916',
  CallEndTime: dotNetDate('2023-08-07T11:09:12'),
  AgentCallDisposition: 'Answered',
  AgentDuration: '4m 12s',
  AgentFirstName: 'Mukesh',
  AgentLastName: 'Kumar',
  CustomerCallDisposition: 'Connected',
  CustomerDuration: '4m 04s',
  OutComeName: 'Interested',
  CallGetWay: 'Twilio',
  DNID: '1140848484',
  IVFlow: 'Sales IVR → Enterprise queue',
  Remarks: 'Discussed licence count and rollout timeline.',
  RecodingUrl: '',
}

export const mockInteractionHistories: InteractionHistory[] = [
  {
    PersonName: 'Bhoomi Mittal',
    ProfileImgFileName: '',
    LeadNo: '100916',
    MobileNo: '+91 9990998798',
    MobileNo1: '+91 9812345678',
    MobileNo2: '',
    FollowupStatus: 'Proposal Sent',
    Remarks: 'Shared the revised quotation; awaiting PO confirmation.',
    CreatedOn: '2023-08-08T16:22:00',
    NextStatusDate: '2023-08-16T10:00:00',
    AmountPaid: '0',
    AssignedTo: 'sneha.k',
    CreatedBy: 'kmukesh343',
    Products: 'CRM, Telephony',
  },
  {
    PersonName: 'Bhoomi Mittal',
    ProfileImgFileName: '',
    LeadNo: '100916',
    MobileNo: '+91 9990998798',
    MobileNo1: '',
    MobileNo2: '',
    FollowupStatus: 'Qualified',
    Remarks: 'Budget confirmed for the current quarter.',
    CreatedOn: '2023-08-02T15:31:00',
    NextStatusDate: '2023-08-08T11:00:00',
    AmountPaid: '0',
    AssignedTo: 'kmukesh343',
    CreatedBy: 'kmukesh343',
    Products: 'CRM',
  },
]

export const mockComments: CommentItem[] = [
  {
    display_name: 'sneha.k',
    remarks: 'Pricing sheet drafted — sharing after the internal review.',
    created_on: '09-Aug-2023 12:04',
    Image: '',
    attachments: [{ FileName: 'pricing_draft.xlsx', FileUrl: '/TempFiles/pricing_draft.xlsx' }],
  },
  {
    display_name: 'kmukesh343',
    remarks: 'Please include the 3-year lock option as a separate line.',
    created_on: '09-Aug-2023 15:41',
    Image: '',
    attachments: [],
  },
]

export const mockHashedUsers: HashedUser[] = [
  { Code: 'U1001', Text: 'kmukesh343' },
  { Code: 'U1002', Text: 'sneha.k' },
  { Code: 'U1003', Text: 'anita.r' },
]

export const mockWaChatLogs: ChatLogItem[] = [
  { id: 'wa1', direction: 'in', message: 'Is the quotation still valid this month?', sentOn: '14-Aug-2023 10:12', sender: 'Bhoomi Mittal' },
  { id: 'wa2', direction: 'out', message: 'Yes, it holds until 31 Aug. Shall I resend the PDF?', sentOn: '14-Aug-2023 10:15', sender: 'sneha.k' },
  { id: 'wa3', direction: 'in', message: 'Please do, thanks!', sentOn: '14-Aug-2023 10:16', sender: 'Bhoomi Mittal' },
]

export const mockCountries: Option[] = [
  { id: '1', name: 'India' },
  { id: '2', name: 'United States' },
  { id: '3', name: 'United Arab Emirates' },
  { id: '4', name: 'United Kingdom' },
]

export const mockStates: Record<string, Option[]> = {
  '1': [
    { id: '11', name: 'Madhya Pradesh' },
    { id: '12', name: 'Delhi' },
    { id: '13', name: 'Rajasthan' },
    { id: '14', name: 'Maharashtra' },
    { id: '15', name: 'Uttar Pradesh' },
  ],
  '2': [
    { id: '21', name: 'California' },
    { id: '22', name: 'Texas' },
  ],
  '3': [{ id: '31', name: 'Dubai' }],
  '4': [{ id: '41', name: 'England' }],
}

export const mockCities: Record<string, Option[]> = {
  '11': [
    { id: '111', name: 'Beohari' },
    { id: '112', name: 'Bhopal' },
    { id: '113', name: 'Indore' },
  ],
  '12': [{ id: '121', name: 'New Delhi' }],
  '13': [
    { id: '131', name: 'Jaipur' },
    { id: '132', name: 'Udaipur' },
  ],
  '14': [
    { id: '141', name: 'Mumbai' },
    { id: '142', name: 'Pune' },
  ],
  '15': [
    { id: '151', name: 'Noida' },
    { id: '152', name: 'Lucknow' },
  ],
  '21': [{ id: '211', name: 'San Francisco' }],
  '22': [{ id: '221', name: 'Austin' }],
  '31': [{ id: '311', name: 'Dubai' }],
  '41': [{ id: '411', name: 'London' }],
}

export const mockSources: Option[] = [
  { id: 'Chat', name: 'Chat' },
  { id: 'Website', name: 'Website' },
  { id: 'Referral', name: 'Referral' },
  { id: 'Cold Call', name: 'Cold Call' },
  { id: 'Exhibition', name: 'Exhibition' },
]

export const mockMediums: Option[] = [
  { id: 'Interested', name: 'Interested' },
  { id: 'Organic', name: 'Organic' },
  { id: 'Paid', name: 'Paid' },
  { id: 'Social', name: 'Social' },
]

export const mockCampaigns: Option[] = [
  { id: 'Summer Outreach', name: 'Summer Outreach' },
  { id: 'Website Enquiry Form', name: 'Website Enquiry Form' },
  { id: 'Diwali Offer', name: 'Diwali Offer' },
]

export const mockFollowupStatuses: Option[] = [
  { id: '1', name: 'Documents Approved' },
  { id: '2', name: 'Followup Required' },
  { id: '3', name: 'Meeting Scheduled' },
  { id: '4', name: 'Proposal Sent' },
  { id: '5', name: 'Not Interested' },
  { id: '6', name: 'Converted' },
]

export const mockUsers: Option[] = [
  { id: '101', name: 'kmukesh343 (Mukesh Kumar)' },
  { id: '102', name: 'sneha.k (Sneha Kapoor)' },
  { id: '103', name: 'rahul.s (Rahul Sharma)' },
  { id: '104', name: 'amit.v (Amit Verma)' },
]

export const mockProducts: Option[] = [
  { id: 'P1', name: 'Kit19 CRM Enterprise Edition' },
  { id: 'P2', name: 'Kit19 WhatsApp Automation Suite' },
  { id: 'P3', name: 'Kit19 Cloud Telephony Integration' },
  { id: 'P4', name: 'Kit19 Lead Generation Webforms' },
]

export const mockSmsSenderIds: Option[] = [
  { id: 'KITNIN', name: 'KITNIN' },
  { id: 'KITCRM', name: 'KITCRM' },
  { id: 'KITSMS', name: 'KITSMS' },
]

export const mockSmsTemplates: Option[] = [
  { id: 'T1', name: 'Followup Reminder Template' },
  { id: 'T2', name: 'Payment Link / Invoice Template' },
  { id: 'T3', name: 'Meeting Confirmation Template' },
]

export const mockEmailTemplates: Option[] = [
  { id: 'E1', name: 'Welcome & Product Brochure' },
  { id: 'E2', name: 'Commercial Proposal & Pricing' },
  { id: 'E3', name: 'Followup & Demo Schedule' },
]

export const mockActionWebforms: Option[] = [
  { id: 'W1', name: 'Lead Intake Form v2' },
  { id: 'W2', name: 'Customer Feedback Survey' },
  { id: 'W3', name: 'Event Registration Form' },
]

